import {
  FileText,
  Upload,
  Download,
  Shield,
  CheckCircle,
  LayoutGrid,
  List,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Pet } from '../types/pet';
import { apiClient } from '@/api/axios';
import { useNotifications } from '../context/NotificationsContext';
import { UploadDocumentModal } from './documents/UploadDocumentModal';
import { UploadChoiceModal } from './documents/UploadChoiceModal';
import { ScanDocumentModal } from './documents/ScanDocumentModal';
import { DocumentPreviewModal } from './documents/DocumentPreviewModal';
import { DashboardHeaderControls } from './DashboardHeaderControls';

const DOCUMENTS_VIEW_KEY = 'petso-documents-view';
type ViewMode = 'grid' | 'list';

const DOCUMENT_TYPE_OPTIONS = [
  { value: '', labelKey: 'documents.filterAll' },
  { value: 'vaccination-record', labelKey: 'documents.types.vaccination' },
  {
    value: 'health-certificate',
    labelKey: 'documents.types.healthCertificate',
  },
  { value: 'dna-report', labelKey: 'documents.types.dnaReport' },
  { value: 'ownership-record', labelKey: 'documents.types.ownershipRecord' },
  {
    value: 'microchip-certificate',
    labelKey: 'documents.types.microchipCertificate',
  },
  {
    value: 'pedigree-certificate',
    labelKey: 'documents.types.pedigreeCertificate',
  },
  { value: 'insurance-document', labelKey: 'documents.types.insurance' },
  { value: 'medical-record', labelKey: 'documents.types.medical' },
  { value: 'lab-result', labelKey: 'documents.types.labResult' },
  { value: 'x-ray-scan', labelKey: 'documents.types.xrayScan' },
  { value: 'surgical-report', labelKey: 'documents.types.surgicalReport' },
  { value: 'dental-record', labelKey: 'documents.types.dentalRecord' },
  { value: 'passport', labelKey: 'documents.types.passport' },
  { value: 'other', labelKey: 'documents.types.other' },
];

interface DocumentManagerProps {
  pet: Pet;
}

interface Document {
  id: string;
  name: string;
  displayName: string;
  fileName: string;
  type: string;
  date: string;
  hash: string;
  verified: boolean;
  issuer?: string;
}

/** Decode base64url (URL-safe base64, no padding) to UTF-8 string. */
function decodeBase64UrlToUtf8(base64url: string): string {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder('utf-8').decode(bytes);
}

function parseKeyForDisplay(key: string): {
  displayName: string;
  fileName: string;
} {
  const pathParts = key.split('/');
  const lastPart = pathParts[pathParts.length - 1] || '';
  // Key format when displayName is set: uid__base64url(displayName)__filename (filename may contain __)
  const segs = lastPart.split('__');
  if (segs.length >= 3) {
    try {
      const base64url = segs[1];
      const fileName = segs.slice(2).join('__');
      const displayName = decodeBase64UrlToUtf8(base64url);
      if (displayName && fileName) {
        return { displayName, fileName };
      }
    } catch {
      // fall through to legacy parsing
    }
  }
  const originalNameIdx = lastPart.indexOf('_');
  const fileName =
    originalNameIdx >= 0 ? lastPart.substring(originalNameIdx + 1) : lastPart;
  return { displayName: fileName, fileName };
}

/** User-facing title: prefer custom document name from upload; else filename (no ext); else empty for "Document". */
function cardDisplayName(displayName: string, fileName: string): string {
  if (displayName && displayName !== fileName) return displayName;
  if (fileName) {
    const withoutExt = fileName.replace(/\.[^/.]+$/, '').trim();
    return withoutExt || fileName;
  }
  return '';
}

function formatDocumentType(type: string): string {
  return type
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function DocumentManager({ pet }: DocumentManagerProps) {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(DOCUMENTS_VIEW_KEY);
      return stored === 'list' ? 'list' : 'grid';
    } catch {
      return 'grid';
    }
  });
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [docToRemove, setDocToRemove] = useState<Document | null>(null);
  const [removing, setRemoving] = useState(false);
  const filteredDocuments =
    typeFilter === ''
      ? documents
      : documents.filter((d) => d.type === typeFilter);

  const persistViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(DOCUMENTS_VIEW_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  const getDocumentIcon = (_type: string) => {
    return <FileText className="w-5 h-5" />;
  };

  const fetchDocuments = async () => {
    try {
      setError(null);
      setLoading(true);
      const res = await apiClient.get<{
        items: {
          key?: string | null;
          name?: string | null;
          size?: number | null;
          lastModified?: string | Date | null;
        }[];
        nextToken?: string;
      }>('/documents');
      const items = res.data.items || [];

      // Exclude profile/pet photos and Social uploads — only show documents.
      // Social uploads use keys/names like "social_..." or paths containing "social/" or "social_".
      const looksLikeSocial = (value: string): boolean => {
        const k = value.replace(/\\/g, '/').trim();
        const lower = k.toLowerCase();
        if (lower.startsWith('social_') || lower.includes('/social_'))
          return true;
        if (lower.includes('/social/')) return true;
        const parts = k.split('/');
        const lastSegment = (parts[parts.length - 1] || '').toLowerCase();
        if (lastSegment.startsWith('social_')) return true;
        // e.g. "prefix_social_1769805140300-uuid" from backend
        if (lastSegment.includes('_social_')) return true;
        return parts.some(
          (p) =>
            p.toLowerCase() === 'social' ||
            p.toLowerCase().startsWith('social_')
        );
      };
      const documentItems = items.filter((i) => {
        if (!i.key || typeof i.key !== 'string') return false;
        if (i.key.includes('/pets/')) return false;
        if (looksLikeSocial(i.key)) return false;
        if (i.name && typeof i.name === 'string' && looksLikeSocial(i.name))
          return false;
        return true;
      });
      const mapped: Document[] = documentItems
        .filter((i) => i.key)
        .map((i) => {
          const key = i.key as string;
          const pathParts = key.split('/');
          const type = pathParts.length > 1 ? pathParts[1] : 'other';
          const { displayName, fileName } = parseKeyForDisplay(key);
          const item = i as {
            key?: string;
            hash?: string;
            onChainHash?: string;
          };
          return {
            id: key,
            name: displayName,
            displayName,
            fileName,
            type,
            date: (i.lastModified
              ? new Date(i.lastModified as string)
              : new Date()
            )
              .toISOString()
              .split('T')[0],
            hash:
              typeof item.hash === 'string' && item.hash
                ? item.hash
                : typeof item.onChainHash === 'string' && item.onChainHash
                  ? item.onChainHash
                  : key,
            verified: false,
            issuer: undefined,
          } as Document;
        });

      // Exclude any document that displays as social (backend key format may vary)
      const isSocialDisplay = (doc: Document): boolean => {
        const n = (doc.name || '').toLowerCase();
        const d = (doc.displayName || '').toLowerCase();
        return n.startsWith('social_') || d.startsWith('social_');
      };
      const filteredMapped = mapped.filter((doc) => !isSocialDisplay(doc));

      filteredMapped.sort((a, b) =>
        a.date < b.date ? 1 : a.date > b.date ? -1 : 0
      );
      setDocuments(filteredMapped);
    } catch (e: unknown) {
      console.error('Failed to fetch documents', e);
      const err = e as {
        message?: string;
        response?: { status?: number; data?: { error?: string } };
      };
      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.error;
      let message = 'Failed to load documents';
      if (status === 502 || status === 503) {
        message =
          serverMessage ||
          'Documents service is temporarily unavailable. Please try again in a moment.';
      } else if (serverMessage) {
        message = serverMessage;
      } else if (err?.message) {
        message = err.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = (data?: { name?: string; type?: string }) => {
    if (data) {
      const docName = data.name?.trim() || 'Document';
      addNotification({
        type: 'document_shared',
        title: 'Document added',
        message: `"${docName}" was uploaded for ${pet.name}.`,
        petId: pet.id,
        petName: pet.name,
        icon: '📄',
        source: 'Documents',
      });
    }
    fetchDocuments();
    setShowUploadModal(false);
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleDownload = async (doc: Document) => {
    try {
      const encodedKey = encodeURIComponent(doc.id);
      const res = await apiClient.get(`/documents/${encodedKey}/download`, {
        responseType: 'blob',
        params: { disposition: 'attachment' },
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e: unknown) {
      console.error('Download failed', e);
      setError(
        (e as { message?: string })?.message || 'Failed to download document'
      );
    }
  };

  const openPreview = (doc: Document) => {
    setPreviewDoc(doc);
  };

  const handleRemove = async () => {
    if (!docToRemove) return;
    setRemoving(true);
    try {
      const encodedKey = encodeURIComponent(docToRemove.id);
      await apiClient.delete(`/documents/${encodedKey}`);
      setDocToRemove(null);
      await fetchDocuments();
    } catch (e: unknown) {
      console.error('Remove failed', e);
      setError(
        (e as { message?: string })?.message || 'Failed to remove document'
      );
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 min-w-0">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h1
            className="text-2xl sm:text-3xl text-[#2D2F7E] dark:text-white min-w-0"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('documents.title')}
          </h1>
          <DashboardHeaderControls />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex rounded-lg border dark:border-white/8 overflow-hidden shrink-0">
              <button
                type="button"
                onClick={() => persistViewMode('grid')}
                className={`p-2.5 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white dark:bg-blue-500'
                    : 'bg-gray-100 dark:bg-[#0F0E16] text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
                aria-label={t('documents.gridView')}
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => persistViewMode('list')}
                className={`p-2.5 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white dark:bg-blue-500'
                    : 'bg-gray-100 dark:bg-[#0F0E16] text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
                aria-label={t('documents.listView')}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
            <label className="flex items-center gap-2 min-w-0 flex-1 sm:flex-initial">
              <span className="text-sm text-gray-600 dark:text-white/70 whitespace-nowrap shrink-0">
                {t('documents.filterByType')}:
              </span>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="flex-1 min-w-0 sm:min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/8 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            type="button"
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity shrink-0"
            onClick={() => setShowChoiceModal(true)}
          >
            <Upload className="w-5 h-5" />
            {t('documents.upload')}
          </button>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-4 mb-6 flex items-start gap-3 min-w-0">
        <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-blue-900 dark:text-blue-300 font-medium">
            {t('documents.blockchainSecuredTitle')}
          </div>
          <div className="text-sm text-blue-700 dark:text-blue-400 break-words">
            {t('documents.blockchainSecuredDesc')}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded p-3 break-words">
          {error.includes('credentials') ||
          error.includes('Could not load credentials')
            ? t('documents.errorLoadCredentials')
            : error}
        </div>
      )}

      {viewMode === 'grid' && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {loading && (
            <div className="col-span-full text-center text-gray-500 dark:text-white/60 py-8">
              {t('common.loading')}
            </div>
          )}
          {!loading && filteredDocuments.length === 0 && (
            <div className="col-span-full text-center text-gray-500 dark:text-white/60 py-8">
              {typeFilter
                ? t('documents.noDocumentsForType')
                : t('documents.noDocuments')}
            </div>
          )}
          {!loading &&
            filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className="bg-white dark:bg-[#16151D] rounded-xl shadow-md hover:shadow-lg transition-shadow border dark:border-white/8 min-w-0 overflow-hidden"
              >
                <div className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white shrink-0">
                      {getDocumentIcon(doc.type)}
                    </div>
                    <div className="flex items-center gap-1">
                      {doc.verified && (
                        <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          <span>{t('documents.verified')}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocToRemove(doc);
                        }}
                        className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-500 hover:text-red-600 dark:text-white/50 dark:hover:text-red-400 transition-colors"
                        title={t('documents.removeDocument')}
                        aria-label={t('documents.removeDocument')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <h3 className="mb-1 dark:text-white font-semibold break-words">
                    {cardDisplayName(doc.displayName, doc.fileName) ||
                      t('documents.unnamedDocument')}
                  </h3>
                  <div className="text-sm text-gray-500 dark:text-white/50 mb-3">
                    {formatDocumentType(doc.type)}
                  </div>

                  <div className="text-sm text-gray-500 dark:text-white/50 mb-4">
                    {t('documents.uploaded')}:{' '}
                    {new Date(doc.date).toLocaleDateString()}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      className="flex-1 flex items-center justify-center gap-2 bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/5 px-3 py-2.5 sm:py-2 rounded-lg text-sm transition-colors dark:text-white"
                      onClick={() => openPreview(doc)}
                      title={t('documents.preview')}
                    >
                      <Search className="w-4 h-4" />
                      {t('documents.preview')}
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-center gap-2 bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/5 px-3 py-2.5 sm:py-2 rounded-lg text-sm transition-colors dark:text-white"
                      onClick={() => handleDownload(doc)}
                      title={t('documents.download')}
                    >
                      <Download className="w-4 h-4" />
                      {t('documents.download')}
                    </button>
                  </div>
                </div>
              </div>
            ))}

          {/* Add Document Card */}
          <div
            className="bg-white dark:bg-[#16151D] rounded-xl shadow-md border-2 border-dashed border-gray-300 dark:border-white/8 hover:border-blue-400 dark:hover:border-blue-700/30 transition-colors cursor-pointer flex items-center justify-center min-h-[260px] sm:min-h-[300px] min-w-0"
            onClick={() => setShowChoiceModal(true)}
          >
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gray-100 dark:bg-[#0F0E16] rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-gray-400 dark:text-white/40" />
              </div>
              <div className="text-gray-600 dark:text-white/70 mb-2">
                {t('documents.uploadNew')}
              </div>
              <div className="text-sm text-gray-500 dark:text-white/50">
                {t('documents.uploadNewHint')}
              </div>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'list' && (
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md border dark:border-white/8 overflow-hidden">
          {loading && (
            <div className="text-center text-gray-500 dark:text-white/60 py-8">
              {t('common.loading')}
            </div>
          )}
          {!loading && filteredDocuments.length === 0 && (
            <div className="text-center text-gray-500 dark:text-white/60 py-8">
              {typeFilter
                ? t('documents.noDocumentsForType')
                : t('documents.noDocuments')}
            </div>
          )}
          {!loading && filteredDocuments.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b dark:border-white/8 bg-gray-50 dark:bg-[#0F0E16]">
                    <th className="px-4 py-3 text-sm font-medium dark:text-white">
                      {t('documents.documentName')}
                    </th>
                    <th className="px-4 py-3 text-sm font-medium dark:text-white">
                      {t('documents.type')}
                    </th>
                    <th className="px-4 py-3 text-sm font-medium dark:text-white w-28 text-right">
                      {t('documents.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr
                      key={doc.id}
                      className="border-b dark:border-white/8 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium dark:text-white">
                          {cardDisplayName(doc.displayName, doc.fileName) ||
                            t('documents.unnamedDocument')}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-white/70">
                        {formatDocumentType(doc.type)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-white/70 transition-colors"
                            onClick={() => openPreview(doc)}
                            title={t('documents.preview')}
                          >
                            <Search className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-white/70 transition-colors"
                            onClick={() => handleDownload(doc)}
                            title={t('documents.download')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-600 dark:text-white/70 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            onClick={() => setDocToRemove(doc)}
                            title={t('documents.removeDocument')}
                            aria-label={t('documents.removeDocument')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && filteredDocuments.length > 0 && (
            <div
              className="border-t dark:border-white/8 p-4 flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              onClick={() => setShowChoiceModal(true)}
            >
              <Upload className="w-5 h-5 text-gray-500 dark:text-white/50" />
              <span className="text-sm text-gray-600 dark:text-white/70">
                {t('documents.uploadNew')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Document Types Info - 2x2 on mobile */}
      <div className="mt-6 sm:mt-8 bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 sm:p-6 border dark:border-white/8 min-w-0">
        <h3 className="text-lg sm:text-xl mb-4 dark:text-white">
          {t('documents.supportedTypes')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[
            {
              titleKey: 'documents.typeCardVaccinationTitle',
              descKey: 'documents.typeCardVaccinationDesc',
            },
            {
              titleKey: 'documents.typeCardHealthTitle',
              descKey: 'documents.typeCardHealthDesc',
            },
            {
              titleKey: 'documents.typeCardDnaTitle',
              descKey: 'documents.typeCardDnaDesc',
            },
            {
              titleKey: 'documents.typeCardOwnershipTitle',
              descKey: 'documents.typeCardOwnershipDesc',
            },
          ].map((item, index) => (
            <div
              key={index}
              className="p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg min-w-0"
            >
              <div className="mb-2 dark:text-white font-medium">
                {t(item.titleKey)}
              </div>
              <div className="text-sm text-gray-600 dark:text-white/70 break-words">
                {t(item.descKey)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showChoiceModal && (
        <UploadChoiceModal
          onClose={() => setShowChoiceModal(false)}
          onChooseUpload={() => {
            setShowChoiceModal(false);
            setShowUploadModal(true);
          }}
          onChooseScan={() => {
            setShowChoiceModal(false);
            setShowScanModal(true);
          }}
        />
      )}

      {showUploadModal && (
        <UploadDocumentModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUpload}
        />
      )}

      {showScanModal && (
        <ScanDocumentModal
          onClose={() => setShowScanModal(false)}
          onUpload={handleUpload}
        />
      )}

      {/* Remove confirm modal - outside view mode so it shows in both grid and list */}
      {docToRemove && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-xl max-w-md w-full p-6 border dark:border-white/8">
            <h3 className="text-lg font-semibold dark:text-white mb-2">
              {t('documents.removeDocument')}
            </h3>
            <p className="text-gray-600 dark:text-white/70 text-sm mb-6">
              {t('documents.removeConfirm')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setDocToRemove(null)}
                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-[#0F0E16] text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={removing}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {removing ? t('common.loading') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewDoc && (
        <DocumentPreviewModal
          document={{
            id: previewDoc.id,
            displayName:
              cardDisplayName(previewDoc.displayName, previewDoc.fileName) ||
              t('documents.unnamedDocument'),
            fileName: previewDoc.fileName,
            type: previewDoc.type,
          }}
          getPreviewUrl={async (key) => {
            const encodedKey = encodeURIComponent(key);
            const res = await apiClient.get(`/documents/${encodedKey}/preview`, {
              responseType: 'blob',
            });
            return URL.createObjectURL(res.data);
          }}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}
